/**
 * Pure numeric helpers behind the settlement scatter charts: the summary
 * statistics in their headers and the symmetric-log x axis they share.
 */

// linear-interpolated quantile of an ascending-sorted sample
export const quantile = (sorted: number[], q: number): number => {
  if (sorted.length === 0) return 0;
  const pos = (sorted.length - 1) * q;
  const base = Math.floor(pos);
  const next = sorted[base + 1];
  return next !== undefined
    ? sorted[base] + (next - sorted[base]) * (pos - base)
    : sorted[base];
};

// round up to the nearest 1/2/5 × 10^k, so the symmetric-log linear zone has a
// tidy width and the ticks land on readable amounts
export const niceRound = (x: number): number => {
  if (x <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(x)));
  const n = x / pow;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * pow;
};

/**
 * Symmetric-log transform centred on 0. Values within ±threshold map linearly
 * onto themselves, so the anchor region stays well spread and readable; beyond
 * it every further decade adds one more `threshold` of axis, compressing
 * extreme values into the tails instead of clipping or piling them. `inverse`
 * undoes `forward`, for turning axis positions back into readable tick labels.
 */
export const symlog = (threshold: number) => {
  const L = threshold;
  const forward = (d: number): number => {
    const a = Math.abs(d);
    return a <= L ? d : Math.sign(d) * (L + L * Math.log10(a / L));
  };
  const inverse = (y: number): number => {
    const a = Math.abs(y);
    return a <= L ? y : Math.sign(y) * L * Math.pow(10, (a - L) / L);
  };
  return { forward, inverse };
};

export const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));

// how far a padded domain reaches past its values, as a share of their span
const DOMAIN_PAD_FRACTION = 0.08;

/**
 * The [min, max] of an axis that holds all `values` with some air on either
 * side, so none of them sits on its edge. Values that all coincide have no span
 * to take a share of, so the padding falls back on their size, or on 1 at zero.
 * An axis that must show a reference value, zero say, passes it among them.
 */
export const paddedDomain = (
  values: readonly number[],
  padFraction = DOMAIN_PAD_FRACTION,
): { min: number; max: number } => {
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const pad = (hi - lo || Math.abs(hi) || 1) * padFraction;
  return { min: lo - pad, max: hi + pad };
};

// a difference under one whole currency unit is rounding noise, not a change
export const isUnchanged = (amount: number): boolean => Math.abs(amount) < 1;

export type Change = "worse" | "unchanged" | "better";

/**
 * Which way a signed difference points, where above zero is the good
 * direction. The one reading of a difference behind a dot's zone, its count in
 * the split bar and the wording of its tooltip, so the three cannot disagree
 * about a difference at or near zero.
 */
export const changeOf = (amount: number): Change =>
  isUnchanged(amount) ? "unchanged" : amount > 0 ? "better" : "worse";
